import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import injectSheet from 'react-jss';
import { connect } from 'react-redux';
import cn from 'classnames';
import contains from 'dom-helpers/query/contains';
import { linkPropertiesType, menuType } from '../../propTypes';
import withGtm from '../withGtm';
import theme, { animatedUnderlineHover, buttonReset, easeOutQuad } from '../../styles/theme';
import Link from '../Link';
import createElementResizeDetectorRef from '../../helpers/createElementResizeDetectorRef';
import { isCurrentBreakpointMobile } from '../../selectors';
import delay from '../../helpers/delay';

const BaseToggle = ({ className, link, ...rest }) => (
  <button className={className} type="button" {...rest}>
    {link.text}
  </button>
);

BaseToggle.propTypes = {
  className: PropTypes.string,
  link: linkPropertiesType.isRequired,
};

BaseToggle.defaultProps = {
  className: undefined,
};

const Toggle = withGtm(BaseToggle);

class Menu extends PureComponent {
  state = {};

  componentDidUpdate(prevProps, prevState) {
    const { isMobile, open } = this.props;
    const { height } = this.state;
    if (open !== prevProps.open || isMobile !== prevProps.isMobile || height !== prevState.height) {
      this.updateStyle();
    }
  }

  // We do not need to anything with the blur, any click out will close the menu
  onBlur = /* istanbul ignore next */ () => {};

  onFocus = () => {
    const { onMenuActivated } = this.props;
    onMenuActivated();
  };

  onMouseEnter = () => {
    const { isMobile, onMenuActivated } = this.props;
    if (!isMobile) {
      this.timeout = delay(onMenuActivated, 100, this.timeout);
    }
  };

  onMouseLeave = () => {
    const { isMobile, onMenuDeactivated } = this.props;
    if (!isMobile) {
      this.timeout = delay(onMenuDeactivated, 100, this.timeout);
    }
  };

  setRef = el => {
    const { receiveClickTester } = this.props;
    if (el) {
      this.containerEl = el;
      this.itemsContainerEl = el.childNodes[1]; // eslint-disable-line prefer-destructuring
      if (receiveClickTester) {
        receiveClickTester(node => contains(this.containerEl, node));
      }
    }
  };

  setElementResizeRef = createElementResizeDetectorRef(({ height }) => {
    this.setState({ height });
  });

  updateStyle() {
    const { isMobile, open } = this.props;
    if (open) {
      const { height } = this.state;
      // Wee need to add 0.5em to the end so that the bottom line is visible
      const desktopHeight = height + 8;
      if (isMobile) {
        this.itemsContainerEl.style.height = `${height}px`;
        this.containerEl.style.transform = '';
      } else {
        this.itemsContainerEl.style.height = `${desktopHeight}px`;
        this.containerEl.style.transform = `translateY(${-desktopHeight}px)`;
      }
    } else {
      this.itemsContainerEl.style.height = '0px';
      this.containerEl.style.transform = '';
    }
  }

  renderSubmenuItems = () => {
    const {
      classes, menu,
    } = this.props;

    return (
      <div className={classes.itemsContainer}>
        <div ref={this.setElementResizeRef}>
          <ul className={classes.items}>
            {menu.submenu.map((item, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <li key={i} className={classes.item}>
                <Link className={classes.link} link={item} />
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  };

  render() {
    const {
      classes, className, menu, onMenuActivated, open,
    } = this.props;

    return (
      <div
        className={classes.menu}
        ref={this.setRef}
        onMouseEnter={this.onMouseEnter}
        onMouseLeave={this.onMouseLeave}
        onFocus={this.onFocus}
        onBlur={this.onBlur}
      >
        <Toggle
          className={cn(className, classes.toggle, open && classes.activeToggle)}
          link={menu}
          onClick={onMenuActivated}
        />
        {this.renderSubmenuItems()}
      </div>
    );
  }
}

Menu.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  className: PropTypes.string,
  isMobile: PropTypes.bool.isRequired,
  menu: menuType.isRequired,
  open: PropTypes.bool,
  onMenuActivated: PropTypes.func,
  onMenuDeactivated: PropTypes.func,
  receiveClickTester: PropTypes.func,
};

Menu.defaultProps = {
  className: undefined,
  onMenuActivated: undefined,
  onMenuDeactivated: undefined,
  open: false,
  receiveClickTester: undefined,
};

const mapStateToProps = state => ({
  isMobile: isCurrentBreakpointMobile(state),
});

export default compose(
  connect(mapStateToProps),
  injectSheet({
    menu: {
      textAlign: 'center',
      letterSpacing: '0.09em',
      position: 'relative',
      transition: `transform 0.3s ${easeOutQuad}`,
      [theme.breakpoints.down('sm')]: {
        fontSize: 16,
        marginBottom: theme.padding.gutter * 1.5,
      },
      '&:last-child': {
        marginBottom: 0,
      },
      [theme.breakpoints.up('md')]: {
        height: '1.5em',
      },
      [theme.breakpoints.up('lg')]: {
        minWidth: 150, // just gives it a bit of a hit zone for the mouse enter and leave
      },
    },
    toggle: {
      ...buttonReset({
        display: 'block',
        lineHeight: 'inherit',
        marginLeft: 'auto',
        marginRight: 'auto',
        fontFamily: 'inherit',
        fontSize: 'inherit',
        fontWeight: 'inherit',
        letterSpacing: 'inherit',
        '&,&:hover,&:focus': {
          background: 'transparent none',
          color: 'inherit',
        },
      }),
      '&::after': {
        right: '0.09em',
      },
    },
    activeToggle: {
      '&::after': {
        content: '""',
        transform: 'scaleX(1)',
        transformOrigin: '0 50%',
      },
    },
    itemsContainer: {
      overflow: 'hidden',
      transition: `height 0.3s ${easeOutQuad}`,
      height: 0,
    },
    items: {
      display: 'block',
      listStyleType: 'none',
      margin: 0,
      padding: 0,
      paddingTop: '1.5em',
      fontSize: 'inherit',
    },
    item: {
      display: 'block',
      margin: 0,
      padding: 0,
      marginBottom: '1em',
      fontSize: 'inherit',
      '&:last-child': {
        marginBottom: 0,
      },
    },
    link: {
      display: 'inline-block',
      fontFamily: theme.fonts.body,
      fontSize: '0.875em',
      fontWeight: theme.fonts.bodyWeight,
      letterSpacing: 'inherit',
      textTransform: 'none',
      '.cms-content &[href]': {
        textDecoration: 'none',
      },
      '&,&:visited,&:hover,&:focus': {
        color: 'inherit',
      },
      [theme.breakpoints.up('md')]: {
        extend: [animatedUnderlineHover, {
          '&::after': {
            bottom: '-0.5em',
            right: 0,
          },
        }],
      },
    },
  }),
)(Menu);
